import { useCallback, useEffect, useRef, useState } from "react";
import type { MediaAssetMeta } from "@care-by-ella/contracts";
import {
  fetchMediaAssets,
  uploadMediaAsset,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useAuth } from "@/auth/AuthContext";

// Media support — the content-image library: every asset with an inline
// preview (served straight off the public route), plus upload/replace via
// file picker. Replace = pick a file for an EXISTING key; the version bumps
// and the new URL busts caches.

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

function fmtBytes(n: number): string {
  return n > 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(n / 1024)} kB`;
}

export function MediaLibraryRoute() {
  const { signOut } = useAuth();
  const [assets, setAssets] = useState<MediaAssetMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState("");
  const [altSv, setAltSv] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const { assets: rows } = await fetchMediaAssets();
      setAssets(rows);
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [signOut]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file || !key.trim() || !altSv.trim()) {
      setError("Nyckel, alt-text och fil krävs.");
      return;
    }
    const mime = file.type;
    if (!["image/jpeg", "image/png", "image/webp"].includes(mime)) {
      setError("Endast JPEG, PNG eller WebP.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      let binary = "";
      for (const b of buf) binary += String.fromCharCode(b);
      await uploadMediaAsset({
        key: key.trim(),
        mimeType: mime as "image/jpeg" | "image/png" | "image/webp",
        dataBase64: btoa(binary),
        altSv: altSv.trim(),
      });
      setKey("");
      setAltSv("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-md">
      <h1 className="font-display text-3xl tracking-tight">Bilder</h1>
      <p className="text-sm text-textSecondary max-w-2xl">
        Innehållsbilder (övningar, recept, illustrationer). Ladda upp med en
        befintlig nyckel för att ersätta — versionen räknas upp och artiklarna
        får den nya bilden.
      </p>

      <div className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-wrap items-end gap-md">
        <label className="flex flex-col gap-xs text-xs text-textSecondary">
          Nyckel (dotted snake)
          <input
            className="border border-surfaceMuted rounded-md px-sm py-xs text-sm font-mono w-72"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="training.pass01.ex01.start"
          />
        </label>
        <label className="flex flex-col gap-xs text-xs text-textSecondary">
          Alt-text (svenska)
          <input
            className="border border-surfaceMuted rounded-md px-sm py-xs text-sm w-72"
            value={altSv}
            onChange={(e) => setAltSv(e.target.value)}
            placeholder="Startposition för övningen"
          />
        </label>
        <input ref={fileRef} type="file" accept="image/*" className="text-sm" />
        <button
          type="button"
          disabled={busy}
          onClick={() => void onUpload()}
          className="bg-brand text-white rounded-md px-md py-xs text-sm disabled:opacity-50"
        >
          {busy ? "Laddar upp…" : "Ladda upp"}
        </button>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {assets && assets.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga bilder uppladdade än.
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-md">
        {assets?.map((a) => (
          <div
            key={a.key}
            className="bg-surface border border-surfaceMuted rounded-md p-sm flex flex-col gap-xs"
          >
            <img
              src={`${API_BASE}/public-api/v1/media/${encodeURIComponent(a.key)}?v=${a.version}`}
              alt={a.altSv}
              loading="lazy"
              className="w-full h-32 object-contain bg-surfaceMuted/40 rounded"
            />
            <span className="font-mono text-xs break-all">{a.key}</span>
            <span className="text-xs text-textSecondary">
              v{a.version} · {fmtBytes(a.byteSize)} · {a.mimeType.slice(6)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
